import axios from 'axios';
import qs from 'qs';
import { authFirebase } from './firebase.config';

const sidebarApiClient = axios.create({
  baseURL: process.env.VITE_SIDEBAR_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => {
    return qs.stringify(params, { encode: true });
  },
});

// Request interceptor to add auth token, created_by/modified_by fields, and required query params
sidebarApiClient.interceptors.request.use(
  async (config) => {
    const user = authFirebase.currentUser;

    if (user) {
      try {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      } catch (error) {
        try {
          const refreshedIdToken = await user.getIdToken(true);
          config.headers.Authorization = `Bearer ${refreshedIdToken}`;
        } catch (refreshError) {
          console.log(refreshError, 'error refreshing Firebase token');
          throw refreshError;
        }
        console.log(error, 'error getting Firebase token');
        throw error;
      }
    }

    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    let userObj = null;
    if (userData) {
      try {
        userObj = JSON.parse(userData);
      } catch (error) {
        console.warn('Could not parse user data from localStorage:', error);
      }
    }

    // Add required query parameters for all requests
    if (userObj) {
      config.params = config.params || {};
      
      // Add user_id and is_admin to all requests
      if (!config.params.user_id) {
        config.params.user_id = userObj.id;
      }
      if (!config.params.is_admin) {
        config.params.is_admin = true; // As specified, all admin routes require admin true
      }
      if (!config.params.is_team) {
        config.params.is_team = false;
      }
      if (!config.params.include_inactive) {
        config.params.include_inactive = true;
      }
    }

    // Add created_by and modified_by fields for POST and PUT requests
    if (config.method === 'post' || config.method === 'put') {
      if (userObj && config.data && typeof config.data === 'object') {
        const userId = userObj.id;
        
        // For POST requests, add both created_by and modified_by
        if (config.method === 'post') {
          config.data.created_by = userId;
          config.data.modified_by = userId;
        }
        // For PUT requests, only add modified_by
        else if (config.method === 'put') {
          config.data.modified_by = userId;
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
sidebarApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 422) {
      // Handle validation errors from the new API
      const validationErrors = error.response.data?.detail || [];
      const errorMessage = validationErrors
        .map((err: any) => `${err.loc?.join('.')}: ${err.msg}`)
        .join(', ');
      
      // You can customize this error handling based on your needs
      console.error('Validation Error:', errorMessage);
      error.message = errorMessage || 'Validation failed';
    }
    
    return Promise.reject(error);
  }
);

// Update auth headers when Firebase auth state changes
authFirebase.onAuthStateChanged(async (user) => {
  if (user) {
    const idToken = await user.getIdToken(true);
    sidebarApiClient.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
  }
});

export default sidebarApiClient; 
